import { useToast } from '@app/core/notifications';
import { DataExporterFactory, useSearchFilter } from '@mi-front/core';
import { saveAs } from 'file-saver';
import merge from 'lodash/merge';
import omit from 'lodash/omit';
import { useEffect, useState } from 'react';
import { useDidUpdateEffect } from '../../hooks/useDidUpdateEffect';
import appHistory from '../../routing/history';
import { ReportError } from '../report-error';
import { ReportType } from '../types';
import { ReportViewProps } from './ReportView';
import { useReportCapture } from './useReportCapture';

const DEFAULT_REPORT_FAILURE_MESSAGE =
  'Ocurrió un error al generar el reporte, por favor intente nuevamente';

function notifyReportFailure(error: unknown, notifyError: (message: string) => void) {
  console.error(error);
  const message = error instanceof ReportError ? error.message : DEFAULT_REPORT_FAILURE_MESSAGE;
  notifyError(message);
}

export type UseReportParams<R extends ReportType> = Pick<
  ReportViewProps<R>,
  | 'defaultValues'
  | 'queryParamsTransform'
  | 'download'
  | 'reportFn'
  | 'getResultCount'
  | 'runOnLoad'
  | 'sort'
  | 'initialResult'
> & {
  skipQuerySyncParams?: string[];
};

export type UseReportResult<R extends ReportType> = {
  formats: string[];
  defaultValues?: R['Values'];
  values: R['Values'];
  result: R['Result'] | null;
  resultCount: number | undefined;
  isLoading: boolean;
  setValues: (values: R['Values']) => void;
  setValue: (partialValues: Partial<R['Values']>, runAfter?: boolean) => void;
  runReport: (values: R['Values']) => Promise<void>;
  refreshReport: () => Promise<void>;
  downloadReport: (format: string) => Promise<void>;
  allowDownload: boolean;
};

function getDefaultResultCount(result: unknown): number | undefined {
  const r = result as { total?: number; metadata?: { totalCount?: number } };
  const total = r?.total ?? r?.metadata?.totalCount;
  return typeof total === 'number' ? total : undefined;
}

export function useReport<R extends ReportType>({
  defaultValues,
  queryParamsTransform,
  download,
  reportFn,
  skipQuerySyncParams = [],
  runOnLoad,
  sort,
  getResultCount,
  initialResult,
}: UseReportParams<R>): UseReportResult<R> {
  const { filter, setFilter } = useSearchFilter(appHistory, {
    initialFilter: defaultValues,
    transform: queryParamsTransform,
  });
  const { notifyError } = useToast();
  const notifyReportRun = useReportCapture();

  const [result, setResult] = useState<R['Result'] | null>(initialResult ?? null);
  const [isLoading, setLoading] = useState(false);
  const [values, setValues] = useState<R['Values']>(filter);

  useDidUpdateEffect(() => {
    setFilter(omit(values, ...skipQuerySyncParams, 'context'));
  }, [values, sort?.id, sort?.desc]);

  // fetch listing on load
  useEffect(() => {
    if (runOnLoad) {
      runReport(values, true);
    }
  }, []);

  useDidUpdateEffect(() => {
    runReport(values);
  }, [sort?.id, sort?.desc]);

  const runReport = async (values: R['Values'], isLoadRun = false) => {
    if (!isLoadRun) {
      notifyReportRun(values);
    }

    setLoading(true);
    setValues(values);

    try {
      const resultResult = await reportFn(values);
      setResult(resultResult);
    } catch (error) {
      notifyReportFailure(error, notifyError);
    } finally {
      setLoading(false);
    }
  };

  const setValue = (partialValues: Partial<R['Values']>, runAfter = true) => {
    const nextValues = merge({}, values, partialValues);

    if (runAfter) {
      runReport(nextValues);
    } else {
      setValues(nextValues);
    }
  };

  const refreshReport = async () => {
    runReport(values);
  };

  const downloadReport = async (format: string) => {
    if (!download) {
      return;
    }

    try {
      if (download.remote) {
        const data = await download.remote(values, format);
        saveAs(new Blob([data as BlobPart]), `${download.filename}.${format}`);
        return;
      }

      const filenameForData = (data: R['Result'] | null): string => {
        return typeof download.filename === 'string' ? download.filename : download.filename(data!);
      };

      if (download.local) {
        const data = result ? result : await reportFn(values);

        const dataToExport = Array.isArray(data)
          ? data
          : download.resultToExport
            ? download.resultToExport(data)
            : [];

        DataExporterFactory.get(format).exportJson(filenameForData(data), dataToExport as object[]);
      } else {
        const data = await reportFn(values, format);

        if (!data) {
          return;
        }

        // If data is a URL, navigate to it
        if (
          typeof data === 'string' &&
          (data.startsWith('http://') || data.startsWith('https://'))
        ) {
          window.location.href = data;
          return;
        }

        const filename = filenameForData(data);

        if (format === 'xls') {
          const blob = new Blob([data as BlobPart], {
            type: 'application/vnd.ms-excel',
          });
          saveAs(blob, `${filename}.xls`);
        } else if (format === 'csv') {
          const blob = new Blob([data as BlobPart], { type: 'text/csv' });
          saveAs(blob, `${filename}.csv`);
        }
      }
    } catch (error) {
      notifyReportFailure(error, notifyError);
    }
  };

  const resultCount =
    result !== null
      ? getResultCount
        ? getResultCount(result)
        : getDefaultResultCount(result)
      : undefined;

  return {
    defaultValues,
    values,
    setValues,
    setValue,
    result,
    resultCount,
    isLoading: isLoading,
    runReport,
    downloadReport,
    allowDownload: !!download,
    formats: ['csv', 'xls'],
    refreshReport,
  };
}
